import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { useAuth } from "@/auth";
import { BrandMark, BrandName, Button, Pill } from "@/components/ui";
import { t } from "@/i18n";
import { roleLabel, statusLabel } from "@/lib/labels";

/** Backend yalniz 'active' uyeligi switch-tenant'a izin verir (auth.py:
 * switch_tenant → uyelik yoksa/aktif degilse PermissionError → 403). Eskiden
 * her satir tiklanabilirdi; 'pending'/'rejected' bir satira tiklamak bu 403'u
 * SESSIZCE yutuyordu (finally blogu switchingId'yi sifirlayip hicbir geri
 * bildirim vermiyordu) — kullanici neden ilerleyemedigini anlamiyordu. */
const CLICKABLE_STATUSES = new Set(["active"]);

/** /tenant-sec — coklu uyelik / super icin tenant secimi (sozlesme §2). */
export function TenantSec() {
  const { principal, switchTenant, status } = useAuth();
  const navigate = useNavigate();
  const [switchingId, setSwitchingId] = useState<number | null>(null);

  if (status === "loading") return null;
  if (principal?.active_tenant) return <Navigate to="/ozet" replace />;

  async function handleSelect(tenantId: number, membershipStatus: string) {
    if (!CLICKABLE_STATUSES.has(membershipStatus)) return;
    setSwitchingId(tenantId);
    try {
      await switchTenant(tenantId);
      navigate("/ozet", { replace: true });
    } finally {
      setSwitchingId(null);
    }
  }

  const memberships = principal?.tenants ?? [];

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-pale to-bg px-4 py-8">
      <div className="w-full max-w-md space-y-4 rounded-2xl border-[1.5px] border-line bg-card p-7 shadow-login sm:p-9">
        <div className="mb-2 flex flex-col items-center text-center">
          <BrandMark size={52} className="mb-3" />
          <BrandName className="text-[22px]" />
        </div>

        <h1 className="text-center font-display text-lg font-bold text-ink">{t("auth.tenant_select.title")}</h1>

        {principal?.user.is_super && (
          <div className="space-y-2 text-center">
            <p className="text-sm text-muted">{t("auth.tenant_select.super_hint")}</p>
            {/* Süper kullanıcı çoğu zaman hiçbir tenant'a üye değildir (canlı bulgu 07.09.2026:
                liste boş kalıyor, /admin'e giden yol yoktu). Mağazaya geçiş /admin tablosundan
                ("Bu mağazaya geç") yapılır. */}
            <Link to="/admin" className="btn btn-primary w-full">
              {t("auth.tenant_select.go_admin")}
            </Link>
          </div>
        )}

        {memberships.length === 0 ? (
          <p className="text-center text-sm text-muted">{t("auth.tenant_select.empty")}</p>
        ) : (
          <ul className="space-y-2">
            {memberships.map((membership) => {
              const clickable = CLICKABLE_STATUSES.has(membership.status);
              return (
                <li key={membership.tenant_id}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => void handleSelect(membership.tenant_id, membership.status)}
                    disabled={!clickable || switchingId === membership.tenant_id}
                    aria-disabled={!clickable}
                    title={clickable ? undefined : statusLabel(membership.status)}
                    className="!flex w-full !justify-between !rounded-card px-4 text-left font-medium disabled:hover:!bg-card"
                  >
                    <span className="truncate">{membership.name}</span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="text-xs text-muted">{roleLabel(membership.role)}</span>
                      {!clickable && <Pill tone="muted">{statusLabel(membership.status)}</Pill>}
                    </span>
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
